import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Users2, BookOpen, Heart, Star, CheckCircle, Circle, ChevronDown, ChevronUp,
  Loader, ArrowLeft, RefreshCw, Save, Calendar, Zap, Quote, MessageSquare,
  PenLine, Send, Lightbulb, Music, Award, Download, Play, Video, Mic
} from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";

export default function FamilyStudyPage() {
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const { t, language } = useLanguage();
  const [study, setStudy]       = useState(null);
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [completing, setCompleting] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [notes, setNotes]       = useState("");
  const [doneQs, setDoneQs]     = useState(new Set());
  const [view, setView]         = useState("study"); // "study" | "history"
  const [justCompleted, setJustCompleted] = useState(false);
  const [childrenWork, setChildrenWork] = useState([]);
  const [childrenWithLessons, setChildrenWithLessons] = useState([]);
  const notesTimeout = useRef(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true); setError("");
    try {
      // Get user's local date for timezone consistency
      const userDate = new Date().toISOString().split('T')[0];
      
      const [sr, hr, work, childrenRes] = await Promise.all([
        axios.get(`${API}/family-study/today?user_date=${userDate}`),
        axios.get(`${API}/family-study/history`),
        axios.get(`${API}/family/children-work`).catch(() => ({ data: [] })),
        axios.get(`${API}/children`).catch(() => ({ data: [] }))
      ]);
      setStudy(sr.data);
      setNotes(sr.data.notes || "");
      setHistory(hr.data);
      setChildrenWork(work.data || []);
      
      // Build children with lessons for parent notes
      const children = childrenRes.data || [];
      const childLessons = await Promise.all(
        children.map(async (child) => {
          try {
            const lr = await axios.get(`${API}/lessons/today/${child.id}?user_date=${userDate}`);
            return { child, lessons: [lr.data] };
          } catch {
            return { child, lessons: [] };
          }
        })
      );
      setChildrenWithLessons(childLessons.filter(c => c.lessons.length > 0));
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load family study.");
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (i) => {
    setDoneQs(prev => {
      const s = new Set(prev);
      s.has(i) ? s.delete(i) : s.add(i);
      return s;
    });
  };

  const handleNotesChange = (val) => {
    setNotes(val);
    if (notesTimeout.current) clearTimeout(notesTimeout.current);
    notesTimeout.current = setTimeout(() => autoSaveNotes(val), 2000);
  };

  const autoSaveNotes = async (val) => {
    if (!study || !val.trim()) return;
    try { await axios.post(`${API}/family-study/${study.id}/notes`, { notes: val }); } catch {}
  };

  const handleComplete = async () => {
    if (!study || study.completed || completing) return;
    setCompleting(true);
    try {
      if (notes.trim()) await axios.post(`${API}/family-study/${study.id}/notes`, { notes });
      const { data } = await axios.post(`${API}/family-study/${study.id}/complete`);
      setStudy(data);
      setJustCompleted(true);
      loadData(); // refresh history
    } catch (e) { console.error(e); }
    finally { setCompleting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#FDF6FF" }}>
      <div className="text-center px-4">
        <Loader size={52} color="#9B5DE5" className="mx-auto animate-spin mb-4" />
        <p className="font-black text-xl text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
          {t('preparing_family_study')}
        </p>
        <p className="text-gray-500 text-sm mt-2">{t('creating_discussion')}</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#FDF6FF" }}>
      <div className="max-w-sm w-full bg-white rounded-3xl p-8 shadow-xl text-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
          <BookOpen size={30} color="white" strokeWidth={2.5} />
        </div>
        <h3 className="font-black text-xl text-[#073B4C] mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
          {t('almost_there')}
        </h3>
        <p className="text-gray-600 text-sm mb-5">{error}</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate("/parent")}
            className="text-white font-bold px-5 py-3 rounded-xl text-sm"
            style={{ background: "#118AB2" }}>
            {t('parent_dashboard')}
          </button>
          <button onClick={loadData}
            className="flex items-center gap-2 bg-gray-100 text-gray-600 font-bold px-5 py-3 rounded-xl text-sm">
            <RefreshCw size={14} /> {t('retry')}
          </button>
        </div>
      </div>
    </div>
  );

  const questions = study?.discussion_questions || [];
  const doneCount = doneQs.size;

  return (
    <div className="min-h-screen pb-8" style={{ background: "#FDF6FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-10"
        style={{ background: "linear-gradient(135deg, #9B5DE5 0%, #EF476F 60%, #FFD166 100%)" }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between mb-6">
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-white/80 hover:text-white font-bold text-sm bg-white/20 px-3 py-1.5 rounded-xl transition-all"
            data-testid="back-btn">
            <ArrowLeft size={16} /> {t('back')}
          </button>
          <div className="flex bg-white/20 rounded-xl p-1 gap-1">
            <button onClick={() => setView("study")}
              className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${view==="study"?"bg-white text-[#9B5DE5]":"text-white"}`}
              data-testid="view-study-btn">{t('study')}</button>
            <button onClick={() => setView("history")}
              className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${view==="history"?"bg-white text-[#9B5DE5]":"text-white"}`}
              data-testid="view-history-btn">{t('history')}</button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white/20 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <Users2 size={40} color="white" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2"
            style={{ fontFamily: "Fredoka, sans-serif", textShadow: "0 2px 10px rgba(0,0,0,0.2)" }}>
            {study?.title || t('family_bible_study')}
          </h1>
          <p className="text-white/80 font-semibold text-sm">
            {new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
          {/* Family streak */}
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-xl px-4 py-2 mt-3">
            <Zap size={16} color="#FFD166" strokeWidth={2.5} />
            <span className="text-white font-black text-sm">{study?.family_streak || 1} {t('day_family_streak')}</span>
          </div>
        </div>
      </div>

      {view === "history" ? (
        <HistoryView history={history} t={t} language={language} />
      ) : (
        <div className="max-w-2xl mx-auto px-4 -mt-5">
          {/* Sing Praises Section */}
          <SingPraisesSection t={t} />

          {/* What kids learned today */}
          {study?.children_lessons?.length > 0 && (
            <div className="bg-white rounded-3xl p-5 shadow-lg mb-4 border-b-4" style={{ borderColor: "#118AB2" }}>
              <h3 className="font-black text-[#073B4C] mb-4 flex items-center gap-2"
                style={{ fontFamily: "Fredoka, sans-serif" }}>
                <BookOpen size={20} color="#118AB2" strokeWidth={2.5} />
                {t('what_kids_learned')}
              </h3>
              <div className="space-y-4">
                {study.children_lessons.map((cl, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden"
                    style={{ background: ["#E0F4FF","#FFF3CD","#E0FFF5","#FFE0EB","#F0E6FF"][i%5] }}>
                    {/* Child info header */}
                    <div className="flex items-center gap-3 p-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center font-black text-white text-sm flex-shrink-0"
                        style={{ background: ["#118AB2","#F77F00","#06D6A0","#EF476F","#9B5DE5"][i%5] }}>
                        {cl.child_name[0]}
                      </div>
                      <div className="flex-1">
                        <p className="font-black text-[#073B4C] text-sm">{cl.child_name}</p>
                        <p className="text-xs text-gray-500 font-medium">{cl.lesson_title} · {cl.verse_reference}</p>
                      </div>
                      {cl.quiz_summary && (
                        <div className="flex items-center gap-1 bg-white/80 px-2 py-1 rounded-full">
                          <CheckCircle size={12} color="#06D6A0" />
                          <span className="text-xs font-bold text-[#06D6A0]">
                            {cl.quiz_summary.correct_count}/{cl.quiz_summary.total_questions}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Quiz answers */}
                    {cl.quiz_summary?.answers && cl.quiz_summary.answers.length > 0 && (
                      <div className="px-3 pb-3">
                        <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">{t('quiz_answers')}</p>
                        <div className="space-y-2">
                          {cl.quiz_summary.answers.map((ans, j) => (
                            <div key={j} className={`text-xs p-2 rounded-lg flex items-start gap-2 ${
                              ans.is_correct ? 'bg-green-100' : 'bg-red-50'
                            }`}>
                              <span className={`flex-shrink-0 mt-0.5 ${ans.is_correct ? 'text-green-600' : 'text-red-500'}`}>
                                {ans.is_correct ? <CheckCircle size={14} /> : <Circle size={14} />}
                              </span>
                              <div>
                                <p className="font-semibold text-gray-700">{ans.question}</p>
                                <p className={`mt-0.5 ${ans.is_correct ? 'text-green-700' : 'text-red-600'}`}>
                                  {ans.is_correct 
                                    ? `✓ ${ans.selected_option}` 
                                    : `✗ ${ans.selected_option || (language === 'es' ? 'Sin respuesta' : 'Not answered')} → ${ans.correct_option}`
                                  }
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Children's Work Section - Drawings and Answers */}
          {childrenWork.length > 0 && (
            <ChildrenWorkSection childrenWork={childrenWork} t={t} />
          )}

          {/* Today's Journal Entries - All written answers from kids */}
          <TodaysJournalEntries childrenWork={childrenWork} t={t} />

          {/* Parent Notes Section */}
          {childrenWithLessons.length > 0 && (
            <ParentNotesSection 
              familyJournals={childrenWithLessons} 
              onNoteSaved={loadData}
              t={t}
            />
          )}

          {/* Opening verse */}
          <div className="rounded-3xl p-6 shadow-lg mb-4 border-b-4"
            style={{ background: "linear-gradient(135deg, #073B4C, #118AB2)", borderColor: "#0a5a7c" }}>
            <Quote size={20} color="white" className="mb-2 opacity-60" />
            <p className="text-white text-lg font-semibold leading-relaxed italic mb-3"
              style={{ fontFamily: "Quicksand, sans-serif" }} data-testid="opening-verse">
              {study?.opening_verse}
            </p>
            <span className="bg-white/20 text-white text-sm font-bold px-3 py-1 rounded-full">
              {study?.verse_reference}
            </span>
          </div>

          {/* Overview */}
          <div className="bg-white rounded-3xl p-5 shadow-sm mb-4 border-l-8" style={{ borderColor: "#06D6A0" }}>
            <h3 className="font-black text-[#073B4C] mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('todays_focus')}
            </h3>
            <p className="text-gray-700 leading-relaxed text-sm" style={{ fontFamily: "Quicksand, sans-serif" }}>
              {study?.overview}
            </p>
          </div>

          {/* Discussion Questions */}
          <div className="bg-white rounded-3xl p-5 shadow-md mb-4 border-b-4" style={{ borderColor: "#9B5DE5" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-black text-[#073B4C] flex items-center gap-2"
                style={{ fontFamily: "Fredoka, sans-serif" }}>
                <MessageSquare size={20} color="#9B5DE5" strokeWidth={2.5} />
                {t('discussion_questions')}
              </h3>
              <span className="bg-[#F0E6FF] text-[#9B5DE5] font-black text-xs px-2 py-1 rounded-full">
                {doneCount}/{questions.length}
              </span>
            </div>
            <div className="space-y-3">
              {questions.map((q, i) => (
                <button key={i} onClick={() => toggleQuestion(i)}
                  className={`w-full text-left rounded-2xl p-4 border-2 transition-all hover:scale-[1.01] active:scale-95 ${doneQs.has(i) ? "border-[#06D6A0] bg-[#E0FFF5]" : "border-gray-100 bg-gray-50 hover:border-[#9B5DE5]"}`}
                  data-testid={`question-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {doneQs.has(i)
                        ? <CheckCircle size={22} color="#06D6A0" strokeWidth={2} />
                        : <Circle size={22} color="#D1D5DB" strokeWidth={2} />}
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold text-sm leading-relaxed ${doneQs.has(i) ? "text-gray-500 line-through" : "text-[#073B4C]"}`}
                        style={{ fontFamily: "Quicksand, sans-serif" }}>
                        {q.question}
                      </p>
                      {q.for_age === "older" && (
                        <span className="mt-1 inline-block bg-[#118AB2] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          {t('older_kids')}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Family Activity/Game */}
          {study?.family_activity && (
            <div className="bg-white rounded-3xl shadow-lg mb-4 overflow-hidden border-b-4" style={{ borderColor: "#06D6A0" }}>
              <div className="p-4" style={{ background: "linear-gradient(135deg, #06D6A0, #4CC9F0)" }}>
                <h3 className="font-black text-white flex items-center gap-2"
                  style={{ fontFamily: "Fredoka, sans-serif" }}>
                  <span className="text-2xl">🎮</span>
                  {t('family_activity_time')}
                </h3>
              </div>
              <div className="p-5">
                <h4 className="font-black text-lg text-[#073B4C] mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {study.family_activity.title}
                </h4>
                <p className="text-gray-700 text-sm leading-relaxed mb-4" style={{ fontFamily: "Quicksand, sans-serif" }}>
                  {study.family_activity.description}
                </p>
                {study.family_activity.materials_needed && study.family_activity.materials_needed.toLowerCase() !== 'none' && (
                  <div className="bg-[#FFF3CD] rounded-xl p-3 mb-3">
                    <p className="text-xs font-bold text-[#F77F00] uppercase tracking-wide mb-1">{t('materials_needed')}</p>
                    <p className="text-sm text-gray-700 font-medium">{study.family_activity.materials_needed}</p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="bg-[#E0FFF5] text-[#06D6A0] text-xs font-bold px-3 py-1 rounded-full capitalize">
                    {study.family_activity.activity_type || 'activity'}
                  </span>
                  <span className="text-xs text-gray-400">5-15 min</span>
                </div>
              </div>
            </div>
          )}

          {/* Family Notes */}
          <div className="bg-white rounded-3xl p-5 shadow-sm mb-5 border-b-4" style={{ borderColor: "#4CC9F0" }}>
            <h3 className="font-black text-[#073B4C] mb-3 flex items-center gap-2"
              style={{ fontFamily: "Fredoka, sans-serif" }}>
              <Save size={18} color="#4CC9F0" strokeWidth={2.5} />
              {t('family_notes')}
            </h3>
            <textarea
              value={notes}
              onChange={e => handleNotesChange(e.target.value)}
              placeholder={t('family_notes_placeholder')}
              rows={4}
              className="w-full border-2 border-gray-100 rounded-2xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#4CC9F0] resize-none transition-colors"
              style={{ fontFamily: "Quicksand, sans-serif" }}
              data-testid="family-notes"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">({t('auto_saved')})</span>
              <button
                onClick={() => {
                  if (notesTimeout.current) clearTimeout(notesTimeout.current);
                  autoSaveNotes(notes);
                  setSaving(true);
                  setTimeout(() => setSaving(false), 1500);
                }}
                disabled={!notes.trim() || saving}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm font-bold transition-all ${
                  saving 
                    ? 'bg-[#06D6A0] text-white' 
                    : 'bg-[#4CC9F0] text-white hover:bg-[#3db8de]'
                } disabled:opacity-50`}
                data-testid="save-notes-btn"
              >
                {saving ? <><CheckCircle size={14} /> {t('saved') || 'Saved!'}</> : <><Save size={14} /> {t('save') || 'Save'}</>}
              </button>
            </div>
          </div>

          {/* Show & Tell: Certificate Presentation */}
          <ShowAndTellSection childrenWithLessons={childrenWithLessons} user={user} t={t} />

          {/* Family Lesson Video */}
          <FamilyLessonVideoSection t={t} />

          {/* Closing Prayer */}
          <div className="rounded-3xl p-5 mb-4 border-b-4"
            style={{ background: "linear-gradient(135deg, #EF476F, #FFD166)", borderColor: "#c8375a" }}>
            <div className="flex items-center gap-2 mb-2">
              <Heart size={20} color="white" strokeWidth={2.5} />
              <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('family_prayer')}
              </h3>
            </div>
            <p className="text-white/90 font-semibold text-sm leading-relaxed"
              style={{ fontFamily: "Quicksand, sans-serif" }} data-testid="prayer-prompt">
              {study?.prayer_prompt}
            </p>
          </div>

          {/* Closing Blessing */}
          <div className="rounded-3xl p-5 mb-4 border-b-4"
            style={{ background: "linear-gradient(135deg, #FFD166, #F77F00)", borderColor: "#e0b040" }}>
            <div className="flex items-center gap-2 mb-2">
              <Star size={20} color="white" strokeWidth={2.5} />
              <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('closing_blessing')}
              </h3>
            </div>
            <p className="text-white/90 font-semibold text-sm leading-relaxed"
              style={{ fontFamily: "Quicksand, sans-serif" }}>
              {study?.closing_thought}
            </p>
          </div>

          {/* Complete / Done */}
          {study?.completed || justCompleted ? (
            <div className="rounded-3xl p-6 text-center mb-4 border-4"
              style={{ background: "#E0FFF5", borderColor: "#06D6A0" }}>
              <CheckCircle size={48} color="#06D6A0" className="mx-auto mb-2" strokeWidth={2} />
              <p className="font-black text-xl text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('family_study_complete')}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {t('great_job_studying')}
              </p>
            </div>
          ) : (
            <button onClick={handleComplete} disabled={completing}
              className="w-full text-white font-black py-5 rounded-2xl border-b-4 active:border-b-0 active:translate-y-1 transition-all text-xl disabled:opacity-60 mb-4"
              style={{ fontFamily: "Fredoka, sans-serif",
                background: "linear-gradient(90deg, #9B5DE5, #EF476F)", borderColor: "#7a3fc3" }}
              data-testid="complete-family-study-btn">
              {completing ? t('saving') : t('complete_family_study')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryView({ history, t, language }) {
  return (
    <div className="max-w-2xl mx-auto px-4 -mt-5">
      <div className="bg-white rounded-3xl p-5 shadow-lg mb-4">
        <h3 className="font-black text-[#073B4C] mb-4 flex items-center gap-2"
          style={{ fontFamily: "Fredoka, sans-serif" }}>
          <Calendar size={20} color="#9B5DE5" strokeWidth={2.5} />
          {t('study_history')}
        </h3>
        {history.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Users2 size={40} className="mx-auto mb-2 opacity-40" />
            <p className="font-medium">{t('no_history_yet')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 rounded-2xl p-3 border-2 transition-all"
                style={h.completed
                  ? { borderColor: "#06D6A0", background: "#E0FFF5" }
                  : { borderColor: "#E5E7EB", background: "#F9FAFB" }}
                data-testid={`history-item-${i}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: h.completed ? "linear-gradient(135deg,#06D6A0,#118AB2)" : "#E5E7EB" }}>
                  {h.completed
                    ? <CheckCircle size={20} color="white" strokeWidth={2} />
                    : <Circle size={20} color="#9CA3AF" strokeWidth={2} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-sm text-[#073B4C] truncate">{h.title}</p>
                  <p className="text-xs text-gray-500 font-medium">
                    {new Date(h.date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { weekday: "short", month: "short", day: "numeric" })}
                    {h.completed && <span className="ml-2 text-[#06D6A0] font-bold">{t('completed')}</span>}
                  </p>
                </div>
                {h.family_streak > 1 && (
                  <div className="flex items-center gap-1 bg-[#FFF3CD] px-2 py-1 rounded-full flex-shrink-0">
                    <Zap size={12} color="#F77F00" strokeWidth={2.5} />
                    <span className="text-xs font-black text-[#F77F00]">{h.family_streak}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Sing Praises Section - Expandable hymn lyrics for family worship
function SingPraisesSection({ t }) {
  const [expanded, setExpanded] = useState(false);
  const [openHymn, setOpenHymn] = useState(null);

  const hymns = [
    {
      title: "There Is Power in the Blood",
      author: "Lewis E. Jones",
      lyrics: [
        { label: "Verse 1", text: "Would you be free from the burden of sin?\nThere's power in the blood, power in the blood;\nWould you o'er evil a victory win?\nThere's wonderful power in the blood." },
        { label: "Chorus", text: "There is power, power, wonder-working power\nIn the blood of the Lamb;\nThere is power, power, wonder-working power\nIn the precious blood of the Lamb." },
        { label: "Verse 2", text: "Would you be free from your passion and pride?\nThere's power in the blood, power in the blood;\nCome for a cleansing to Calvary's tide;\nThere's wonderful power in the blood." },
        { label: "Verse 3", text: "Would you be whiter, much whiter than snow?\nThere's power in the blood, power in the blood;\nSin stains are lost in its life-giving flow;\nThere's wonderful power in the blood." },
        { label: "Verse 4", text: "Would you do service for Jesus your King?\nThere's power in the blood, power in the blood;\nWould you live daily His praises to sing?\nThere's wonderful power in the blood." },
      ]
    },
    {
      title: "Nothing but the Blood of Jesus",
      subtitle: "Who Can Wash Away My Sins?",
      author: "Robert Lowry",
      lyrics: [
        { label: "Verse 1", text: "What can wash away my sin?\nNothing but the blood of Jesus;\nWhat can make me whole again?\nNothing but the blood of Jesus." },
        { label: "Chorus", text: "Oh! precious is the flow\nThat makes me white as snow;\nNo other fount I know,\nNothing but the blood of Jesus." },
        { label: "Verse 2", text: "For my pardon, this I see,\nNothing but the blood of Jesus;\nFor my cleansing this my plea,\nNothing but the blood of Jesus." },
        { label: "Verse 3", text: "Nothing can for sin atone,\nNothing but the blood of Jesus;\nNaught of good that I have done,\nNothing but the blood of Jesus." },
        { label: "Verse 4", text: "This is all my hope and peace,\nNothing but the blood of Jesus;\nThis is all my righteousness,\nNothing but the blood of Jesus." },
      ]
    }
  ];

  return (
    <div className="bg-white rounded-3xl shadow-lg mb-4 overflow-hidden border-b-4" style={{ borderColor: "#FFD166" }}>
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, #FFD166, #F77F00)" }}
        data-testid="sing-praises-toggle"
      >
        <div className="text-left">
          <div className="flex items-center gap-2">
            <Music size={22} color="white" strokeWidth={2.5} />
            <h3 className="font-black text-white text-lg" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('sing_praises')}
            </h3>
          </div>
          <p className="text-white/80 text-xs mt-1">
            {t('start_with_worship')}
          </p>
        </div>
        <div className="bg-white/20 rounded-full p-2">
          {expanded ? <ChevronUp size={20} color="white" /> : <ChevronDown size={20} color="white" />}
        </div>
      </button>

      {expanded && (
        <div className="p-4 space-y-3">
          {hymns.map((hymn, i) => (
            <div key={i} className="rounded-2xl border-2 overflow-hidden transition-all"
              style={{ borderColor: openHymn === i ? "#FFD166" : "#f0f0f0" }}
              data-testid={`hymn-${i}`}>
              {/* Hymn title - clickable */}
              <button
                onClick={() => setOpenHymn(openHymn === i ? null : i)}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-yellow-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: i === 0 ? "linear-gradient(135deg, #EF476F, #9B5DE5)" : "linear-gradient(135deg, #118AB2, #06D6A0)" }}>
                  <Music size={18} color="white" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                    {hymn.title}
                  </p>
                  {hymn.subtitle && (
                    <p className="text-xs font-bold text-purple-500 italic">{hymn.subtitle}</p>
                  )}
                  <p className="text-xs text-gray-400">{hymn.author}</p>
                </div>
                <div className="bg-gray-100 rounded-full p-1.5">
                  {openHymn === i ? <ChevronUp size={16} color="#F77F00" /> : <ChevronDown size={16} color="#999" />}
                </div>
              </button>

              {/* Lyrics */}
              {openHymn === i && (
                <div className="px-4 pb-4 space-y-3">
                  {hymn.lyrics.map((section, j) => (
                    <div key={j} className="rounded-xl p-3"
                      style={{ background: section.label === "Chorus" ? "#FFF3CD" : "#FAFAFA" }}>
                      <p className="text-[10px] font-black uppercase tracking-wider mb-1.5"
                        style={{ color: section.label === "Chorus" ? "#F77F00" : "#999" }}>
                        {section.label}
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line"
                        style={{ fontFamily: "'Quicksand', sans-serif" }}>
                        {section.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Children's Work Section - Shows drawings and answers from lessons AND tasks (EXPANDABLE)
function ChildrenWorkSection({ childrenWork, t }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedChildren, setExpandedChildren] = useState({});

  // Count total items
  const totalDrawings = childrenWork.reduce((sum, item) => 
    sum + (item.drawings?.length || 0) + 
    (item.task_responses?.filter(r => r.drawing_data)?.length || 0), 0);
  const totalAnswers = childrenWork.reduce((sum, item) => 
    sum + (item.answers?.length || 0) + 
    (item.task_responses?.filter(r => r.answer_content)?.length || 0), 0);

  const toggleChild = (childId) => {
    setExpandedChildren(prev => ({
      ...prev,
      [childId]: !prev[childId]
    }));
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg mb-4 overflow-hidden border-b-4" style={{ borderColor: "#9B5DE5" }}>
      {/* Header - Clickable to expand/collapse all */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}
        data-testid="children-work-toggle"
      >
        <div className="text-left">
          <h3 className="font-black text-white flex items-center gap-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
            <PenLine size={20} color="white" strokeWidth={2.5} />
            Children's Work Today
          </h3>
          <p className="text-white/80 text-xs mt-1">
            {totalDrawings} drawing{totalDrawings !== 1 ? 's' : ''} · {totalAnswers} written response{totalAnswers !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="bg-white/20 rounded-full p-2">
          {expanded ? <ChevronUp size={20} color="white" /> : <ChevronDown size={20} color="white" />}
        </div>
      </button>

      {/* Expandable content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {childrenWork.map((item, i) => {
            const childId = item.child?.id || i;
            const isChildExpanded = expandedChildren[childId];
            const hasContent = (item.drawings?.length > 0) || (item.answers?.length > 0) || (item.task_responses?.length > 0);
            
            if (!hasContent) return null;

            return (
              <div key={i} className="rounded-2xl overflow-hidden" style={{ background: ["#F0E6FF","#E0FFF5","#FFE4E6","#E0F4FF"][i%4] }}>
                {/* Child header - clickable */}
                <button 
                  onClick={() => toggleChild(childId)}
                  className="w-full p-4 flex items-center gap-2"
                  data-testid={`child-work-toggle-${i}`}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-white text-sm flex-shrink-0"
                    style={{ background: ["#9B5DE5","#06D6A0","#EF476F","#118AB2"][i%4] }}>
                    {item.child?.name?.[0] || "?"}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-black text-[#073B4C] text-sm">{item.child?.name}</p>
                    <p className="text-xs text-gray-500">
                      {item.lesson?.title || "Activities"} · 
                      {item.drawings?.length > 0 && ` ${item.drawings.length} drawing${item.drawings.length > 1 ? 's' : ''}`}
                      {item.answers?.length > 0 && ` ${item.answers.length} answer${item.answers.length > 1 ? 's' : ''}`}
                    </p>
                  </div>
                  <div className="text-gray-400">
                    {isChildExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>
                
                {/* Child's content - expandable */}
                {isChildExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    {/* Lesson drawings */}
                    {item.drawings?.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-xs font-bold text-[#9B5DE5] uppercase tracking-wider">Lesson Drawings</p>
                        {item.drawings.map((drawing, j) => (
                          <div key={j} className="bg-white rounded-xl p-3">
                            <p className="text-xs font-bold text-gray-500 mb-2 flex items-center gap-1">
                              <PenLine size={12} />
                              {drawing.activity_type === "fun_idea" ? "Fun Idea Drawing" : 
                               drawing.activity_type === "lesson" ? "Lesson Drawing" : "Drawing"}
                            </p>
                            <img 
                              src={drawing.drawing_data} 
                              alt={`${item.child?.name}'s drawing`}
                              className="w-full rounded-lg border border-gray-200"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Lesson written answers */}
                    {item.answers?.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-xs font-bold text-[#118AB2] uppercase tracking-wider">Lesson Reflections</p>
                        {item.answers.map((answer, j) => {
                          const prompt = item.lesson?.reflection_prompts?.[answer.prompt_index] || `Question ${answer.prompt_index + 1}`;
                          return (
                            <div key={j} className="bg-white rounded-xl p-3">
                              <p className="text-xs font-bold text-gray-500 mb-1 flex items-center gap-1">
                                <Lightbulb size={12} />
                                {prompt}
                              </p>
                              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "Quicksand, sans-serif" }}>
                                "{answer.content}"
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Task responses (drawings and answers) */}
                    {item.task_responses?.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-xs font-bold text-[#EF476F] uppercase tracking-wider">Activity Responses</p>
                        {item.task_responses.map((resp, j) => {
                          const task = item.tasks?.[resp.task_index];
                          return (
                            <div key={j} className="bg-white rounded-xl p-3">
                              <p className="text-xs font-bold text-[#EF476F] mb-2">
                                {task?.title || `Activity ${resp.task_index + 1}`}
                              </p>
                              {resp.answer_content && (
                                <div className="mb-2">
                                  <p className="text-xs text-gray-500 mb-1">Written Response:</p>
                                  <p className="text-sm text-gray-700" style={{ fontFamily: "Quicksand, sans-serif" }}>
                                    "{resp.answer_content}"
                                  </p>
                                </div>
                              )}
                              {resp.drawing_data && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Drawing:</p>
                                  <img 
                                    src={resp.drawing_data} 
                                    alt={`${item.child?.name}'s activity drawing`}
                                    className="w-full rounded-lg border border-gray-200"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Today's Journal Entries - Shows all written answers from kids today (EXPANDABLE)
function TodaysJournalEntries({ childrenWork, t }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState({});

  // Collect all journal entries (written answers) from all children
  const allEntries = [];
  
  childrenWork.forEach((item) => {
    const childName = item.child?.name || "Child";
    const childAge = item.child?.age;
    const lessonTitle = item.lesson?.title || "";
    
    // Add lesson answers/reflections
    if (item.answers?.length > 0) {
      item.answers.forEach((answer) => {
        const prompt = item.lesson?.reflection_prompts?.[answer.prompt_index] || "";
        allEntries.push({
          childName,
          childAge,
          type: "reflection",
          lessonTitle,
          prompt,
          content: answer.content,
          createdAt: answer.created_at
        });
      });
    }
    
    // Add task responses
    if (item.task_responses?.length > 0) {
      item.task_responses.forEach((resp) => {
        if (resp.answer_content) {
          const task = item.tasks?.[resp.task_index];
          allEntries.push({
            childName,
            childAge,
            type: "activity",
            taskTitle: task?.title || `Activity ${resp.task_index + 1}`,
            content: resp.answer_content,
            createdAt: resp.answer_updated_at || resp.created_at
          });
        }
      });
    }
    
    // Add journal entries (My Journal from child dashboard)
    if (item.journal_entries?.length > 0) {
      item.journal_entries.forEach((entry) => {
        allEntries.push({
          childName,
          childAge,
          type: "journal",
          content: entry.content,
          createdAt: entry.created_at
        });
      });
    }
  });

  // Sort by time
  allEntries.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Group entries by type for summary
  const reflectionCount = allEntries.filter(e => e.type === "reflection").length;
  const journalCount = allEntries.filter(e => e.type === "journal").length;
  const activityCount = allEntries.filter(e => e.type === "activity").length;

  const toggleEntry = (index) => {
    setExpandedEntries(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg mb-4 overflow-hidden border-b-4" style={{ borderColor: "#118AB2" }}>
      {/* Header - Clickable to expand/collapse */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, #118AB2, #06D6A0)" }}
        data-testid="journal-entries-toggle"
      >
        <div className="text-left">
          <div className="flex items-center gap-2">
            <PenLine size={20} color="white" strokeWidth={2.5} />
            <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Today's Journal Entries
            </h3>
          </div>
          <p className="text-white/80 text-xs mt-1">
            {allEntries.length} entr{allEntries.length !== 1 ? 'ies' : 'y'}
            {reflectionCount > 0 && ` · ${reflectionCount} reflection${reflectionCount > 1 ? 's' : ''}`}
            {journalCount > 0 && ` · ${journalCount} journal`}
            {activityCount > 0 && ` · ${activityCount} activit${activityCount > 1 ? 'ies' : 'y'}`}
          </p>
        </div>
        <div className="bg-white/20 rounded-full p-2">
          {expanded ? <ChevronUp size={20} color="white" /> : <ChevronDown size={20} color="white" />}
        </div>
      </button>
      
      {/* Expandable content */}
      {expanded && (
        <div className="p-4">
          {allEntries.length > 0 ? (
            <div className="space-y-3">
              {allEntries.map((entry, i) => {
                const isEntryExpanded = expandedEntries[i];
                const previewText = entry.content.length > 80 
                  ? entry.content.substring(0, 80) + "..." 
                  : entry.content;

                return (
                  <div key={i} className="rounded-2xl overflow-hidden border-l-4"
                    style={{ 
                      background: entry.type === "reflection" ? "#E0F4FF" : 
                                  entry.type === "journal" ? "#F0E6FF" : "#FFF9E6",
                      borderColor: entry.type === "reflection" ? "#118AB2" : 
                                   entry.type === "journal" ? "#9B5DE5" : "#F77F00"
                    }}>
                    {/* Entry header - clickable */}
                    <button 
                      onClick={() => toggleEntry(i)}
                      className="w-full p-3 flex items-center gap-2"
                      data-testid={`journal-entry-toggle-${i}`}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-white text-sm flex-shrink-0"
                        style={{ background: entry.type === "reflection" ? "#118AB2" : 
                                             entry.type === "journal" ? "#9B5DE5" : "#F77F00" }}>
                        {entry.childName?.[0] || "?"}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-black text-sm text-[#073B4C]">{entry.childName}</p>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white ${
                            entry.type === "reflection" ? "bg-[#118AB2]" : 
                            entry.type === "journal" ? "bg-[#9B5DE5]" : "bg-[#F77F00]"
                          }`}>
                            {entry.type === "reflection" ? "Reflection" : 
                             entry.type === "journal" ? "Journal" : "Activity"}
                          </span>
                        </div>
                        {!isEntryExpanded && (
                          <p className="text-xs text-gray-600 truncate mt-1">"{previewText}"</p>
                        )}
                      </div>
                      <div className="text-gray-400 flex-shrink-0">
                        {isEntryExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </button>
                    
                    {/* Entry content - expandable */}
                    {isEntryExpanded && (
                      <div className="px-3 pb-3">
                        <p className="text-xs text-gray-500 mb-2">
                          {entry.type === "reflection" ? (
                            <>Lesson: {entry.lessonTitle}</>
                          ) : entry.type === "journal" ? (
                            <>Personal Journal</>
                          ) : (
                            <>Activity: {entry.taskTitle}</>
                          )}
                        </p>
                        
                        {entry.prompt && (
                          <p className="text-xs text-gray-500 italic mb-2 bg-white/50 p-2 rounded-lg">
                            Q: {entry.prompt}
                          </p>
                        )}
                        
                        <div className="bg-white rounded-xl p-3">
                          <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "Quicksand, sans-serif" }}>
                            "{entry.content}"
                          </p>
                        </div>
                        
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <PenLine size={28} className="text-gray-400" />
              </div>
              <p className="font-bold text-gray-500">No journal entries yet today</p>
              <p className="text-xs text-gray-400 mt-1">
                Children's written reflections and answers will appear here
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Parent Notes Section
function ParentNotesSection({ familyJournals, onNoteSaved, t }) {
  const [selectedChild, setSelectedChild] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [noteType, setNoteType] = useState("encouragement");
  const [saving, setSaving] = useState(false);
  const [existingNotes, setExistingNotes] = useState([]);

  // Collect all children and their lessons
  const childrenWithLessons = familyJournals.filter(f => f.lessons?.length > 0);

  useEffect(() => {
    // Collect existing parent notes
    const notes = [];
    familyJournals.forEach(f => {
      if (f.parent_notes?.length) {
        f.parent_notes.forEach(n => {
          notes.push({ ...n, childName: f.child?.name });
        });
      }
    });
    setExistingNotes(notes);
  }, [familyJournals]);

  const handleSaveNote = async () => {
    if (!selectedChild || !noteContent.trim()) return;
    setSaving(true);
    try {
      const childData = childrenWithLessons.find(c => c.child?.id === selectedChild);
      const lessonId = childData?.lessons?.[0]?.id;
      
      if (lessonId) {
        await axios.post(`${API}/parent-notes`, {
          lesson_id: lessonId,
          child_id: selectedChild,
          content: noteContent,
          note_type: noteType
        });
        setNoteContent("");
        onNoteSaved?.();
      }
    } catch (e) {
      console.error("Failed to save parent note:", e);
    } finally {
      setSaving(false);
    }
  };

  if (childrenWithLessons.length === 0) return null;

  return (
    <div className="bg-white rounded-3xl shadow-lg mb-4 overflow-hidden border-b-4" style={{ borderColor: "#118AB2" }}>
      <div className="p-4" style={{ background: "linear-gradient(135deg, #118AB2, #073B4C)" }}>
        <h3 className="font-black text-white flex items-center gap-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
          <MessageSquare size={20} color="white" strokeWidth={2.5} />
          Parent's Notes
        </h3>
        <p className="text-white/80 text-xs mt-1">Write encouragement or discussion notes for your children</p>
      </div>
      <div className="p-4 space-y-4">
        {/* Existing notes */}
        {existingNotes.length > 0 && (
          <div className="space-y-2 pb-4 border-b">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Your Notes Today</p>
            {existingNotes.map((note, i) => (
              <div key={i} className="bg-blue-50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    note.note_type === "encouragement" ? "bg-green-100 text-green-700" :
                    note.note_type === "prayer" ? "bg-purple-100 text-purple-700" :
                    "bg-blue-100 text-blue-700"
                  }`}>
                    {note.note_type === "encouragement" ? "Encouragement" :
                     note.note_type === "prayer" ? "Prayer" : "Discussion"}
                  </span>
                  <span className="text-xs text-gray-500">for {note.childName}</span>
                </div>
                <p className="text-sm text-gray-700">{note.content}</p>
              </div>
            ))}
          </div>
        )}

        {/* Add new note */}
        <div className="space-y-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Add a Note</p>
          
          {/* Select child */}
          <div className="flex flex-wrap gap-2">
            {childrenWithLessons.map((item, i) => (
              <button
                key={i}
                onClick={() => setSelectedChild(item.child?.id)}
                className={`px-3 py-2 rounded-xl text-sm font-bold transition-all ${
                  selectedChild === item.child?.id
                    ? "bg-[#118AB2] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {item.child?.name}
              </button>
            ))}
          </div>

          {selectedChild && (
            <>
              {/* Note type */}
              <div className="flex gap-2">
                {[
                  { type: "encouragement", label: "Encouragement", color: "#06D6A0" },
                  { type: "discussion", label: "Discussion", color: "#118AB2" },
                  { type: "prayer", label: "Prayer", color: "#9B5DE5" }
                ].map(({ type, label, color }) => (
                  <button
                    key={type}
                    onClick={() => setNoteType(type)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      noteType === type
                        ? "text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                    style={noteType === type ? { background: color } : {}}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Note content */}
              <textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                className="w-full p-3 rounded-xl border-2 border-gray-200 focus:border-[#118AB2] outline-none resize-none text-sm"
                rows={3}
                placeholder={
                  noteType === "encouragement" ? "Great job on today's lesson! I'm so proud of you..." :
                  noteType === "prayer" ? "I'm praying for you about..." :
                  "Let's talk more about this lesson..."
                }
              />

              <button
                onClick={handleSaveNote}
                disabled={saving || !noteContent.trim()}
                className="w-full py-3 rounded-xl font-black text-white bg-gradient-to-r from-[#118AB2] to-[#06D6A0] disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ fontFamily: "Fredoka, sans-serif" }}
              >
                {saving ? <Loader size={18} className="animate-spin" /> : <Send size={18} />}
                Save Note
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// Show & Tell Section - Kids present certificates and share what they learned
function ShowAndTellSection({ childrenWithLessons, user, t }) {
  const [expanded, setExpanded] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(null);
  const [shareText, setShareText] = useState({});

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      const { data: children } = await axios.get(`${API}/children`);
      const certs = [];
      for (const child of children) {
        try {
          const { data } = await axios.get(`${API}/achievements/certificates/check/${child.id}`);
          if (data.certificate_earned && data.certificate) {
            certs.push({ ...data.certificate, childName: child.name, childId: child.id });
          }
        } catch {}
      }
      setCertificates(certs);
    } catch {}
  };

  const handlePrint = (cert) => {
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Bible Buds Certificate</title>
      <style>
        body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f0f0f0; }
        .cert { width: 800px; padding: 60px; background: white; border: 8px solid #9B5DE5; border-radius: 20px; text-align: center; font-family: Georgia, serif; position: relative; }
        .cert::before { content: ''; position: absolute; inset: 8px; border: 3px solid #FFD166; border-radius: 14px; }
        h1 { color: #9B5DE5; font-size: 42px; margin-bottom: 8px; }
        h2 { color: #073B4C; font-size: 28px; margin: 24px 0 8px; }
        .name { color: #EF476F; font-size: 36px; font-weight: bold; margin: 16px 0; border-bottom: 3px solid #EF476F; display: inline-block; padding-bottom: 4px; }
        .theme { color: #118AB2; font-size: 20px; margin: 12px 0; }
        .scripture { color: #666; font-style: italic; font-size: 16px; margin: 8px 0 24px; }
        .stars { font-size: 32px; margin: 16px 0; }
        .date { color: #999; font-size: 14px; margin-top: 32px; }
        @media print { body { background: white; } }
      </style></head><body>
      <div class="cert">
        <div class="stars">&#9733; &#9733; &#9733; &#9733; &#9733;</div>
        <h1>Bible Buds</h1>
        <h2>Certificate of Completion</h2>
        <p style="color:#666;font-size:18px;">This certifies that</p>
        <div class="name">${cert.childName || cert.child_name}</div>
        <p style="color:#666;font-size:18px;">has completed all lessons and activities for</p>
        <div class="theme">Week ${cert.week_number}: ${cert.theme}</div>
        <div class="scripture">${cert.scripture}</div>
        <div class="stars">&#9733; &#9733; &#9733; &#9733; &#9733;</div>
        <div class="date">${new Date(cert.earned_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
      </div>
      <script>window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg mb-4 overflow-hidden border-b-4" style={{ borderColor: "#FFD166" }}
      data-testid="show-and-tell-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, #FFD166, #F77F00)" }}
        data-testid="show-tell-toggle"
      >
        <div className="text-left">
          <h3 className="font-black text-white flex items-center gap-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
            <Award size={20} color="white" strokeWidth={2.5} />
            Show & Tell Time!
          </h3>
          <p className="text-white/80 text-xs mt-1">
            Kids present their certificates and share what they learned
          </p>
        </div>
        <div className="bg-white/20 rounded-full p-2">
          {expanded ? <ChevronUp size={20} color="white" /> : <ChevronDown size={20} color="white" />}
        </div>
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {certificates.length === 0 ? (
            <div className="text-center py-6 bg-yellow-50 rounded-2xl">
              <Award size={40} color="#F77F00" className="mx-auto mb-2 opacity-50" />
              <p className="text-gray-500 font-medium text-sm">
                No certificates earned yet this week!
              </p>
              <p className="text-gray-400 text-xs mt-1">
                Kids need to complete all lessons & activities to earn a certificate
              </p>
            </div>
          ) : (
            certificates.map((cert, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border-2 border-yellow-200"
                style={{ background: "#FFFBEB" }}
                data-testid={`show-tell-cert-${i}`}
              >
                {/* Certificate card */}
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-white text-sm"
                      style={{ background: ["#9B5DE5","#06D6A0","#EF476F","#118AB2"][i%4] }}>
                      {cert.childName?.[0] || "?"}
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                        {cert.childName}'s Certificate
                      </p>
                      <p className="text-xs text-gray-500">
                        Week {cert.week_number}: {cert.theme}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePrint(cert)}
                        className="bg-[#9B5DE5] text-white px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1 hover:scale-105 active:scale-95 transition-all"
                        data-testid={`print-show-tell-${i}`}
                      >
                        <Download size={12} /> Print
                      </button>
                    </div>
                  </div>

                  {/* Scripture */}
                  <div className="bg-white rounded-xl p-3 mb-3 border border-yellow-100">
                    <p className="text-sm text-gray-600 italic">"{cert.scripture}"</p>
                  </div>

                  {/* What I learned - text area */}
                  <div className="bg-white rounded-xl p-3 border border-yellow-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Mic size={14} color="#F77F00" />
                      <p className="text-xs font-bold text-orange-600 uppercase tracking-wide">
                        What {cert.childName} learned this week:
                      </p>
                    </div>
                    <textarea
                      value={shareText[cert.childId] || ""}
                      onChange={(e) => setShareText(prev => ({ ...prev, [cert.childId]: e.target.value }))}
                      placeholder={`${cert.childName}, tell everyone what you learned about ${cert.theme}!`}
                      rows={3}
                      className="w-full border-2 border-yellow-100 rounded-xl px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-orange-300 resize-none transition-colors"
                      data-testid={`show-tell-textarea-${i}`}
                    />
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Prompt for parents */}
          <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
            <p className="text-xs text-orange-600 font-medium">
              <Lightbulb size={12} className="inline mr-1" />
              <strong>Tip:</strong> Let each child stand up, hold their certificate, and share one thing they learned this week. Celebrate their achievement!
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Family Lesson Video Section
function FamilyLessonVideoSection({ t }) {
  const [videoData, setVideoData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/family/weekly-lesson`)
      .then(res => setVideoData(res.data))
      .catch(() => {});
  }, []);

  if (!videoData?.video) return null;

  const video = videoData.video;
  const embedUrl = video.embed_url || video.youtube_url?.replace("watch?v=", "embed/");

  return (
    <div className="bg-white rounded-3xl shadow-lg mb-4 overflow-hidden border-b-4" style={{ borderColor: "#118AB2" }}
      data-testid="family-video-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, #118AB2, #06D6A0)" }}
        data-testid="family-video-toggle"
      >
        <div className="text-left">
          <h3 className="font-black text-white flex items-center gap-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
            <Video size={20} color="white" strokeWidth={2.5} />
            Family Lesson Video
          </h3>
          <p className="text-white/80 text-xs mt-1">
            {video.title || "Watch this week's family lesson together"}
          </p>
        </div>
        <div className="bg-white/20 rounded-full p-2">
          {expanded ? <ChevronUp size={20} color="white" /> : <ChevronDown size={20} color="white" />}
        </div>
      </button>

      {expanded && (
        <div className="p-4">
          {embedUrl && (
            <div className="relative rounded-2xl overflow-hidden mb-3" style={{ paddingTop: "56.25%" }}>
              <iframe
                src={embedUrl}
                title={video.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          <div className="bg-blue-50 rounded-xl p-3">
            <p className="font-bold text-sm text-[#118AB2]">{video.title}</p>
            {video.description && (
              <p className="text-xs text-gray-500 mt-1">{video.description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
